import { createTheme } from "@mui/material/styles";
import { getActiveOverlayContainer } from "../constants/overlay";

export const palette = {
  primary: {
    50: "#E7F5F1",
    100: "#CEEBE4",
    200: "#B6E1D6",
    300: "#6CC3AD",
    400: "#3BAF91",
    500: "#0A9B76",
    base: "#0A9B76",
    600: "#087C5E",
    700: "#065D47",
    800: "#043E2F",
    900: "#032F23",
    950: "#01100C",
  },
  info: "#1667AD",
  infoLight: "#E1EAF2",
  success: "#2F9741",
  successLight: "#E4EFE7",
  warning: "#D08700",
  warningLight: "#F4EEE1",
  error: "#AB2424",
  errorLight: "#F0E4E5",
  blue: "#3A95FF",
  neutral: {
    0: "#FFFFFF",
    5: "#F5F5F5",
    10: "#EBEBEB",
    20: "#D5D5D5",
    30: "#BCBCBC",
    40: "#9FA0A0",
    50: "#7C7D7D",
    base: "#7C7D7D",
    60: "#6F7070",
    70: "#606161",
    80: "#4E4F4F",
    90: "#373838",
    95: "#272828",
    100: "#000000",
  },
};

// Extend the theme to include our custom palette
declare module "@mui/material/styles" {
  interface Palette {
    neutral: typeof palette.neutral;
  }

  interface PaletteOptions {
    neutral?: typeof palette.neutral;
  }
}

// Create a theme instance.
const theme = createTheme({
  palette: {
    primary: palette.primary,
    secondary: {
      main: "#C5060B",
    },
    error: {
      main: palette.error,
    },
    success: {
      main: "#56C568",
      light: "#6FCF97",
    },
    background: {
      default: "#fff",
    },
    text: {
      primary: palette.neutral[90],
      secondary: palette.neutral[50],
      disabled: palette.neutral[30],
    },
    neutral: palette.neutral,
  },
  typography: {
    fontFamily: `'Open Sans', 'Helvetica', 'Arial', sans-serif`,
    fontSize: 14,
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 600,
    h1: {
      fontSize: 32,
      fontWeight: 400,
      lineHeight: "16px",
    },
    h2: {
      fontSize: 28,
      fontWeight: 400,
      lineHeight: "16px",
    },
    h3: {
      fontSize: 24,
      fontWeight: 400,
      lineHeight: "16px",
    },
    h4: {
      fontSize: 20,
      fontWeight: 400,
      lineHeight: "16px",
    },
    h5: {
      fontSize: 18,
      fontWeight: 400,
      lineHeight: "16px",
    },
    body1: {
      fontSize: 16,
      fontWeight: 400,
      lineHeight: "16px",
    },
    body2: {
      fontSize: 14,
      fontWeight: 400,
      lineHeight: "16px",
    },
    caption: {
      fontSize: 12,
      fontWeight: 400,
      lineHeight: "16px",
    },
    overline: {
      fontSize: 10,
      fontWeight: 400,
      lineHeight: "16px",
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          margin: 0,
          padding: 0,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
        },
      },
    },
    MuiDialog: {
      defaultProps: {
        container: getActiveOverlayContainer,
      },
      styleOverrides: {
        root: {
          position: "absolute",
          inset: 0,
        },
        container: {
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
        },
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          position: "absolute",
        },
      },
    },
  },
});

export default theme;
